import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { getAssessmentReport } from '../api/api';
import { PageContainer, Card } from '../components/ui';

const RISK_COLORS = {
  LOW: 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-500/15 dark:text-green-400 dark:border-green-500/30',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-500/15 dark:text-yellow-400 dark:border-yellow-500/30',
  HIGH: 'bg-orange-100 text-orange-800 border border-orange-200 dark:bg-orange-500/15 dark:text-orange-400 dark:border-orange-500/30',
  CRITICAL: 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30',
};

export default function Report() {
  const { organizationId, assessmentId } = useParams();
  const { t, language } = useLanguage();
  const locale = (language === 'en-US' || language === 'en') ? 'en-US' : 'pt-BR';
  const RISK_LABELS = { LOW: t.low, MEDIUM: t.medium, HIGH: t.high, CRITICAL: t.critical };
  const SEVERITY_LABELS = { LOW: t.severityLow, MEDIUM: t.severityMedium, HIGH: t.severityHigh, CRITICAL: t.severityCritical };
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getAssessmentReport(organizationId, assessmentId)
      .then(setReport)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [organizationId, assessmentId]);

  if (loading) {
    return (
      <Layout>
        <PageContainer>
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0ea5e9]" />
          </div>
        </PageContainer>
      </Layout>
    );
  }

  if (error || !report) {
    return (
      <Layout>
        <PageContainer>
          <div className="bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30 p-4 rounded-[10px]">{error || t.reportNotFound}</div>
          <Link to={`/org/${organizationId}/dashboard`} className="inline-block mt-4 text-[#0ea5e9] hover:text-[#0284c7] font-medium transition duration-200">
            {t.backToDashboard}
          </Link>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageContainer>
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#0f172a] dark:text-slate-100">{t.securityReport}</h1>
            <p className="text-[#475569] dark:text-slate-400 mt-1">
              {t.assessmentOf} {new Date(report.createdAt).toLocaleString(locale)}
            </p>
          </div>
          <Link
            to={`/org/${organizationId}/dashboard`}
            className="px-4 py-2 text-[#0ea5e9] hover:text-[#0284c7] hover:bg-[#0ea5e9]/10 rounded-[10px] font-medium transition duration-200"
          >
            {t.backToDashboard}
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <h2 className="text-[#475569] dark:text-slate-400 font-medium mb-1">{t.securityScore}</h2>
            <p className="text-4xl font-bold text-[#0ea5e9]">{report.score}</p>
            <p className="text-[#475569] dark:text-slate-400 text-sm mt-1">{t.of100Points}</p>
          </Card>
          <Card className="p-6">
          <h2 className="text-[#475569] dark:text-slate-400 font-medium mb-1">{t.riskLevel}</h2>
          <span className={`inline-block px-4 py-2 rounded-[10px] text-lg font-medium ${RISK_COLORS[report.riskLevel] || 'bg-slate-500/15 text-slate-400 border border-slate-500/30'}`}>
            {RISK_LABELS[report.riskLevel] || report.riskLevel}
          </span>
          </Card>
        </div>

        {report.vulnerabilities?.length > 0 ? (
          <Card className="p-6 mb-8">
            <h2 className="text-lg font-semibold text-[#0f172a] dark:text-slate-100 mb-4">{t.vulnerabilitiesDetectedTitle}</h2>
            <div className="space-y-4">
              {report.vulnerabilities.map((v) => (
                <div
                  key={v.id}
                  className="p-4 rounded-[10px] border-l-4 border-red-500/50 bg-red-500/10"
                >
                  <h3 className="font-semibold text-[#0f172a] dark:text-slate-100">{v.title}</h3>
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/30 mt-1">
                    {SEVERITY_LABELS[v.severity] || v.severity}
                  </span>
                  {v.description && (
                    <p className="text-[#475569] dark:text-slate-400 mt-2 text-sm">{v.description}</p>
                  )}
                  {v.recommendation && (
                    <p className="text-[#0ea5e9] mt-2 text-sm font-medium">
                      {t.recommendation}: {v.recommendation}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card className="p-6 mb-8 bg-green-500/5 border-green-500/30">
            <h2 className="text-lg font-semibold text-green-800 dark:text-green-400">{t.noCriticalVulnerabilities}</h2>
            <p className="text-green-700 dark:text-green-400 mt-1">
              {t.congratsNoVulns}
            </p>
          </Card>
        )}

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-[#0f172a] dark:text-slate-100 mb-4">{t.securityRecommendations}</h2>
          <ul className="list-disc list-inside space-y-2 text-[#475569] dark:text-slate-400">
            {t.reportRecommendations?.map((rec, i) => (
              <li key={i}>{rec}</li>
            ))}
          </ul>
        </Card>
      </PageContainer>
    </Layout>
  );
}
